/**
 * GazeWeave Global State (Zustand)
 */

import { create } from 'zustand'
import type {
  AppMode,
  GazeWeaveDocument,
  GazeState,
  ReadingSettings,
  RenderedLine,
} from '@/types'

interface GazeWeaveStore {
  // App state
  mode: AppMode
  setMode: (mode: AppMode) => void

  // Document
  document: GazeWeaveDocument | null
  setDocument: (doc: GazeWeaveDocument | null) => void

  // Layout
  lines: RenderedLine[]
  totalHeight: number
  scrollY: number
  setLayout: (lines: RenderedLine[], totalHeight: number) => void
  setScrollY: (y: number) => void

  // Gaze
  gazeState: GazeState
  setGazeState: (state: GazeState) => void
  trackingMode: 'webcam' | 'mouse' | 'off'
  setTrackingMode: (mode: 'webcam' | 'mouse' | 'off') => void

  // Settings
  settings: ReadingSettings
  updateSettings: (partial: Partial<ReadingSettings>) => void
}

const defaultSettings: ReadingSettings = {
  fontSize: 18,
  lineHeight: 36, // generous spacing for eye tracking accuracy
  fontFamily: 'Inter',
  columnWidth: 680,
  gazeHighlightEnabled: true,
  gazeHighlightIntensity: 0.7,
  autoScrollEnabled: false,
  autoScrollSpeed: 1,
  theme: 'dark',
}

const defaultGazeState: GazeState = {
  currentPoint: null,
  fixationLine: null,
  fixationParagraph: null,
  dwellStartTime: null,
  isFixating: false,
  velocity: 0,
}

export const useStore = create<GazeWeaveStore>((set) => ({
  mode: 'welcome',
  setMode: (mode) => set({ mode }),

  document: null,
  setDocument: (document) => set({ document, mode: document ? 'reading' : 'welcome' }),

  lines: [],
  totalHeight: 0,
  scrollY: 0,
  setLayout: (lines, totalHeight) => set({ lines, totalHeight }),
  setScrollY: (scrollY) => set({ scrollY }),

  gazeState: defaultGazeState,
  setGazeState: (gazeState) => set({ gazeState }),
  trackingMode: 'off',
  setTrackingMode: (trackingMode) => set({ trackingMode }),

  settings: defaultSettings,
  updateSettings: (partial) =>
    set((state) => ({ settings: { ...state.settings, ...partial } })),
}))
